<template>
  <!-- Modal backdrop -->
  <div 
    v-if="isVisible" 
    class="modal-backdrop fade"
    :class="{ show: isVisible }"
    @click="closeModal"
  ></div>
  
  <!-- Modal -->
  <div
    class="modal fade"
    :class="{ show: isVisible }"
    :style="{ display: isVisible ? 'block' : 'none' }"
    tabindex="-1"
    aria-labelledby="difficultyModalLabel"
    :aria-hidden="!isVisible"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 id="difficultyModalLabel" class="modal-title">Select Difficulty</h5>
          <button
            type="button"
            class="btn-close"
            @click="closeModal"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <p class="text-muted mb-3">
            Override the calculated difficulty for this hike
          </p>
          <div class="difficulty-options">
            <button
              v-for="level in difficultyLevels"
              :key="level.value"
              class="difficulty-option"
              :class="{ active: selectedDifficulty === level.value }"
              :style="{ borderColor: selectedDifficulty === level.value ? level.color : 'transparent' }"
              @click="selectDifficulty(level.value)"
            >
              <span class="difficulty-name" :style="{ color: level.color }">
                {{ level.label }}
              </span>
              <small class="difficulty-description">{{ level.description }}</small>
            </button>
          </div>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            @click="clearOverride"
          >
            Use Calculated
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="closeModal"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from "vue";
import { useStore } from "vuex";

const store = useStore();
const isVisible = ref(false);

const props = defineProps({
  currentDifficulty: {
    type: String,
    default: null
  },
  calculatedDifficulty: {
    type: String,
    required: true
  }
});

const emit = defineEmits(["update:selected", "close"]);

const selectedDifficulty = ref(props.currentDifficulty || props.calculatedDifficulty);

const difficultyLevels = [
  {
    value: 'Easy',
    label: 'Easy',
    color: '#4CAF50',
    description: 'A gentle route suitable for most fitness levels'
  },
  {
    value: 'Medium',
    label: 'Medium',
    color: '#FF9800',
    description: 'Some uphill sections, requires moderate fitness'
  },
  {
    value: 'Difficult',
    label: 'Difficult',
    color: '#F44336',
    description: 'Steep climbs and challenging terrain, good fitness needed'
  },
  {
    value: 'Extra Difficult',
    label: 'Extra Difficult',
    color: '#9C27B0',
    description: 'Very steep and demanding, only for experienced hikers'
  }
];

function selectDifficulty(value) {
  selectedDifficulty.value = value;
  emit("update:selected", value);
  store.commit('planTrail/setDifficultyOverride', value);
}

function clearOverride() {
  selectedDifficulty.value = props.calculatedDifficulty;
  emit("update:selected", null);
  store.commit('planTrail/setDifficultyOverride', null);
  closeModal();
}

function closeModal() {
  isVisible.value = false;
  // Remove modal-open class from body
  document.body.classList.remove('modal-open');
  emit('close');
}

function show() {
  isVisible.value = true;
  selectedDifficulty.value = props.currentDifficulty || props.calculatedDifficulty;
  // Add modal-open class to body
  document.body.classList.add('modal-open');
}

// Handle ESC key
function handleKeydown(e) {
  if (e.key === 'Escape' && isVisible.value) {
    closeModal();
  }
}

// Add/remove keydown listener based on visibility
watch(isVisible, (newVal) => {
  if (newVal) {
    document.addEventListener('keydown', handleKeydown);
  } else {
    document.removeEventListener('keydown', handleKeydown);
  }
});

defineExpose({ show });
</script>

<style scoped>
.modal-content {
  background-color: #1b1b1d;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.modal-header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.modal-footer {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-close {
  filter: invert(1);
  opacity: 0.8;
}

.difficulty-options {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.difficulty-option {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
  text-align: left;
}

.difficulty-option:hover {
  background: rgba(255, 255, 255, 0.08);
}

.difficulty-option.active {
  background: rgba(255, 255, 255, 0.1);
}

.difficulty-name {
  font-weight: 600;
  font-size: 1.1rem;
  margin-bottom: 0.25rem;
}

.difficulty-description {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.875rem;
}

.btn-secondary {
  background-color: transparent;
  border-color: rgba(255, 255, 255, 0.2);
  color: #fff;
}

.btn-secondary:hover {
  background-color: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.3);
}

.btn-primary {
  background-color: #ffd600;
  border-color: #ffd600;
  color: #000;
}

.btn-primary:hover {
  background-color: #ffca00;
  border-color: #ffca00;
}

.text-muted {
  color: rgba(255, 255, 255, 0.6) !important;
}
</style>